import { useParams } from "react-router-dom";
import { Label } from "@groot/ui/label";
import {
  OrderStatusBadge,
  PaymentStatusBadge,
  RequestStatusBadge,
} from "@/components/status-badges";
import {
  useOrder,
  useUpdateOrder,
  useUpdateOrderItems,
  useDeleteOrder,
} from "@/pages/order/hooks/useOrders";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { NumberInput } from "@/components/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@groot/ui/primitives";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit2,
  Save,
  Plus,
  Minus,
  Copy,
  Eye,
  Share2,
  ExternalLink,
  Trash2,
  FileText,
  SearchX,
  Package,
  Camera,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@groot/ui/dialog";
import { useCreatePhotoRequest } from "@/pages/photo-request/hooks/usePhotoRequests";
import { useActiveProducts } from "@/pages/product/hooks/useProducts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Skeleton } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { metaLabelClass, statClass } from "@groot/ui/typography";
import { TrackingCard } from "./components/TrackingCard";

/** A totals entry: label/amount row on phones, right-aligned column from `sm` up. */
const totalLineClass = "flex items-baseline justify-between sm:block sm:text-right";

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const orderId = parseInt(id || "0", 10);
  const { data: order, isLoading } = useOrder(orderId);
  const updateOrder = useUpdateOrder();
  const updateOrderItems = useUpdateOrderItems();
  const createPhotoRequest = useCreatePhotoRequest();
  const deleteOrderMutation = useDeleteOrder();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: products } = useActiveProducts();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingItems, setIsEditingItems] = useState(false);
  const [formData, setFormData] = useState({
    status: "new" as "new" | "printing" | "shipped" | "delivered" | "cancelled",
    discountType: "" as "" | "flat" | "percentage",
    discountValue: 0,
    discountReason: "",
    paymentStatus: "not_paid" as "not_paid" | "paid",
    paymentDate: "",
    paymentMethod: "UPI",
    expectedDeliveryDate: "",
    notes: "",
    shippingProvider: "" as "" | "speedpost",
    trackingId: "",
    shippingCost: 60,
  });
  const [orderItems, setOrderItems] = useState<
    Array<{
      productName: string;
      size: string;
      quantity: number;
      unitPrice: number;
      printFormat?: "mini" | "medium" | "large" | null;
    }>
  >([]);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <PageLayout title="Order" description="Loading...">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout title="Order" description="Not found">
        <EmptyState
          icon={SearchX}
          title="Order not found"
          description="This order may have been deleted or the link is invalid."
          action={
            <Button variant="outline" asChild>
              <Link to="/orders">
                <ArrowLeft className="size-4" />
                Back to Orders
              </Link>
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const handleEdit = () => {
    setFormData({
      status: order.status,
      discountType: order.discountType || "",
      discountValue: order.discountValue || 0,
      discountReason: order.discountReason || "",
      paymentStatus: order.paymentStatus,
      paymentDate: order.paymentDate ? new Date(order.paymentDate).toISOString().split("T")[0] : "",
      paymentMethod: order.paymentMethod || "UPI",
      expectedDeliveryDate: order.expectedDeliveryDate
        ? new Date(order.expectedDeliveryDate).toISOString().split("T")[0]
        : "",
      notes: order.notes || "",
      shippingProvider: order.shippingProvider === "speedpost" ? "speedpost" : "",
      trackingId: order.trackingId || "",
      shippingCost: Number(order.shippingCost ?? 60),
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateOrder.mutateAsync({
        id: orderId,
        data: {
          status: formData.status,
          discountType: formData.discountType || undefined,
          discountValue: formData.discountValue || undefined,
          discountReason: formData.discountReason || undefined,
          paymentStatus: formData.paymentStatus,
          paymentDate: formData.paymentDate || undefined,
          paymentMethod: formData.paymentMethod,
          expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
          notes: formData.notes || undefined,
          shippingProvider: formData.shippingProvider || undefined,
          trackingId: formData.trackingId || undefined,
          shippingCost: formData.shippingCost,
        },
      });
      toast.success("Success", { description: "Order updated" });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to update order" });
    }
  };

  const handleEditItems = () => {
    setOrderItems(
      order.items?.map((item) => ({
        productName: item.productName,
        size: item.size,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        printFormat: item.printFormat ?? null,
      })) || [],
    );
    setFormData((prev) => ({
      ...prev,
      discountType: order.discountType || "",
      discountValue: order.discountValue || 0,
      discountReason: order.discountReason || "",
    }));
    setIsEditingItems(true);
  };

  const handleSaveItems = async () => {
    try {
      await updateOrderItems.mutateAsync({
        id: orderId,
        items: orderItems,
      });
      // Also update discount and shipping cost if changed
      if (
        formData.discountType !== (order.discountType || "") ||
        formData.discountValue !== (order.discountValue || 0) ||
        formData.discountReason !== (order.discountReason || "") ||
        formData.shippingCost !== Number(order.shippingCost ?? 60)
      ) {
        await updateOrder.mutateAsync({
          id: orderId,
          data: {
            discountType: formData.discountType || undefined,
            discountValue: formData.discountValue || undefined,
            discountReason: formData.discountReason || undefined,
            shippingCost: formData.shippingCost,
          },
        });
      }
      toast.success("Success", { description: "Order items updated" });
      setIsEditingItems(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to update order items" });
    }
  };

  const handleCreatePhotoRequest = async () => {
    try {
      await createPhotoRequest.mutateAsync({
        orderId,
        customerId: order.customerId,
      });
      toast.success("Success", { description: "Photo request created" });
      setPhotoDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to create photo request" });
    }
  };

  const copyPublicLink = (link: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/public/photo-requests/${link}`);
    toast.success("Copied", { description: "Public link copied to clipboard" });
  };

  const copyOrderPublicLink = () => {
    if (!order?.publicLink) return;
    navigator.clipboard.writeText(`${window.location.origin}/public/orders/${order.publicLink}`);
    toast.success("Copied", { description: "Order public link copied to clipboard" });
  };

  const handleDeleteOrder = async () => {
    const confirmed = await confirm({
      title: "Delete order?",
      description:
        "This will permanently delete this order along with all its items, photo requests, and uploaded files. This action cannot be undone.",
      confirmLabel: "Delete Order",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await deleteOrderMutation.mutateAsync(orderId);
      toast.success("Deleted", { description: "Order removed" });
      navigate("/orders");
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete order" });
    }
  };

  const addItem = () => {
    setOrderItems([...orderItems, { productName: "", size: "", quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: string, value: string | number) => {
    const updated = [...orderItems];
    updated[index] = { ...updated[index], [field]: value };
    setOrderItems(updated);
  };

  const subtotal = order.items?.reduce((sum, item) => sum + Number(item.lineTotal), 0) ?? 0;
  const discountType = order.discountType;
  const discountValue = Number(order.discountValue ?? 0);
  const discountAmount = Number(order.discountAmount ?? 0);
  const shippingCost = Number(order.shippingCost ?? 0);
  const total = Number(order.total ?? 0);

  return (
    <PageLayout
      title={order.orderNumber}
      description={`Order for ${order.customer?.name}`}
      actions={
        <div className="flex flex-wrap gap-2">
          {isEditing ? (
            <Button onClick={handleSave} disabled={updateOrder.isPending}>
              <Save className="size-4" />
              Save
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleEdit}>
                <Edit2 className="size-4" />
                Edit
              </Button>
              <Button variant="outline" asChild>
                <Link to={`/orders/${order.id}/invoice`}>
                  <FileText className="size-4" />
                  Invoice
                </Link>
              </Button>
              <Button variant="outline" onClick={copyOrderPublicLink}>
                <Share2 className="size-4" />
                Share Order Link
              </Button>
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={handleDeleteOrder}
                disabled={deleteOrderMutation.isPending}
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            </>
          )}
          <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">Request Photos</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Photo Request</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                This will generate a public link for the customer to upload photos.
              </p>
              <Button onClick={handleCreatePhotoRequest} disabled={createPhotoRequest.isPending}>
                {createPhotoRequest.isPending ? "Creating..." : "Create Request"}
              </Button>
            </DialogContent>
          </Dialog>
          <Button variant="outline" asChild>
            <Link to="/orders">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Order Information */}
        <Card>
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      setFormData({ ...formData, status: value as typeof formData.status })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="printing">Printing</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Status</Label>
                  <Select
                    value={formData.paymentStatus}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        paymentStatus: value as typeof formData.paymentStatus,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_paid">Not Paid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Discount</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.discountType}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          discountType: value as typeof formData.discountType,
                        })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat ₹</SelectItem>
                        <SelectItem value="percentage">%</SelectItem>
                      </SelectContent>
                    </Select>
                    <NumberInput
                      placeholder="Value"
                      value={formData.discountValue}
                      onValueChange={(v) => setFormData({ ...formData, discountValue: v })}
                      className="flex-1"
                    />
                  </div>
                  <Input
                    placeholder="Reason (optional)"
                    value={formData.discountReason}
                    onChange={(e) => setFormData({ ...formData, discountReason: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Input
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={formData.paymentDate}
                    onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected Delivery Date</Label>
                  <Input
                    type="date"
                    value={formData.expectedDeliveryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expectedDeliveryDate: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shipping Provider</Label>
                  <Select
                    value={formData.shippingProvider}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        shippingProvider: value as typeof formData.shippingProvider,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="speedpost">SpeedPost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tracking ID</Label>
                  <Input
                    value={formData.trackingId}
                    onChange={(e) => setFormData({ ...formData, trackingId: e.target.value })}
                    placeholder="e.g. ET057096757IN"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shipping Cost (₹)</Label>
                  <NumberInput
                    value={formData.shippingCost}
                    onValueChange={(v) => setFormData({ ...formData, shippingCost: v })}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
                <div>
                  <p className={metaLabelClass}>Status</p>
                  <div className="mt-1">
                    <OrderStatusBadge status={order.status} />
                  </div>
                </div>
                <div>
                  <p className={metaLabelClass}>Payment Status</p>
                  <div className="mt-1">
                    <PaymentStatusBadge status={order.paymentStatus} />
                  </div>
                </div>
                <div>
                  <p className={metaLabelClass}>Customer</p>
                  <p className="mt-1 text-sm text-foreground">
                    <Link
                      to={`/customers/${order.customerId}`}
                      className="font-medium hover:text-primary"
                    >
                      {order.customer?.name}
                    </Link>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {order.customer?.mobileNumber ?? ""}
                  </p>
                </div>
                <div>
                  <p className={metaLabelClass}>Order Date</p>
                  <p className="mt-1 text-sm text-foreground">
                    {new Date(order.orderDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className={metaLabelClass}>Discount</p>
                  <p
                    className={`mt-1 text-sm font-medium ${discountAmount > 0 ? "text-destructive" : "text-foreground"}`}
                  >
                    {discountAmount > 0
                      ? `-₹${discountAmount}${discountType === "percentage" ? ` (${discountValue}%)` : ""}${order.discountReason ? ` — ${order.discountReason}` : ""}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className={metaLabelClass}>Shipping Cost</p>
                  <p className="mt-1 text-sm font-medium text-foreground">₹{shippingCost}</p>
                </div>
                <div>
                  <p className={metaLabelClass}>Payment Method</p>
                  <p className="mt-1 text-sm text-foreground">{order.paymentMethod ?? "—"}</p>
                </div>
                {order.paymentDate && (
                  <div>
                    <p className={metaLabelClass}>Payment Date</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(order.paymentDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {order.expectedDeliveryDate && (
                  <div>
                    <p className={metaLabelClass}>Expected Delivery</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(order.expectedDeliveryDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {order.notes && (
                  <div className="md:col-span-2 pt-2 border-t">
                    <p className={metaLabelClass}>Notes</p>
                    <p className="mt-1 text-sm text-muted-foreground">{order.notes}</p>
                  </div>
                )}
                {order.shippingProvider && order.trackingId && (
                  <div className="md:col-span-2 pt-2 border-t">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className={metaLabelClass}>Shipping</p>
                        <p className="mt-1 text-sm text-foreground font-medium capitalize">
                          {order.shippingProvider}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {order.trackingId}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-primary"
                        onClick={() => {
                          const payload = JSON.stringify({
                            t: order.trackingId,
                            c: order.shippingProvider,
                          });
                          const encoded = btoa(payload);
                          window.open(
                            `https://speedposttrack.io/tracking-result?d=${encodeURIComponent(encoded)}`,
                            "_blank",
                          );
                        }}
                      >
                        <ExternalLink className="size-4" />
                        Live Tracking
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shipment Tracking */}
        {order.trackingId && order.status !== "cancelled" && (
          <TrackingCard trackingNumber={order.trackingId} orderId={order.id} />
        )}

        {/* Order Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Order Items</CardTitle>
            {isEditingItems ? (
              <Button size="sm" onClick={handleSaveItems} disabled={updateOrderItems.isPending}>
                <Save className="size-4" />
                Save Items
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleEditItems}>
                <Edit2 className="size-4" />
                Edit Items
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {isEditingItems ? (
              <div className="space-y-0">
                {/* Item cards */}
                <div className="px-4 py-3 space-y-3">
                  {orderItems.map((item, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 space-y-1">
                          <Select
                            value={item.productName}
                            onValueChange={(value) => {
                              const product = products?.find((p) => p.name === value);
                              if (product) {
                                const updated = [...orderItems];
                                updated[index] = {
                                  ...updated[index],
                                  productName: product.name,
                                  size: product.size,
                                  unitPrice: Number(product.unitPrice),
                                  printFormat: product.printFormat ?? null,
                                };
                                setOrderItems(updated);
                              }
                            }}
                          >
                            <SelectTrigger className="h-9 text-sm font-medium border border-dashed border-border bg-muted/50 hover:bg-muted/50 hover:border-foreground/30">
                              <SelectValue placeholder="Select product..." />
                            </SelectTrigger>
                            <SelectContent>
                              {products?.map((product) => (
                                <SelectItem key={product.id} value={product.name}>
                                  {product.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {item.size && (
                            <p className="text-xs text-muted-foreground pl-1">{item.size}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-9 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                          onClick={() => removeItem(index)}
                        >
                          <Minus className="size-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs font-normal text-muted-foreground">
                            Unit Price (₹)
                          </Label>
                          <NumberInput
                            value={item.unitPrice}
                            onValueChange={(v) => updateItem(index, "unitPrice", v)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-normal text-muted-foreground">
                            Quantity
                          </Label>
                          <NumberInput
                            value={item.quantity}
                            onValueChange={(v) => updateItem(index, "quantity", v)}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                      <div className="text-right text-sm font-semibold text-foreground">
                        ₹
                        {(
                          (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
                          (Number.isNaN(item.quantity) ? 0 : item.quantity)
                        ).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Item Bar */}
                <div className="px-4 py-3 bg-muted/50 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary/80 font-medium"
                    onClick={addItem}
                  >
                    <Plus className="size-4" />
                    Add Item
                  </Button>
                </div>

                {/* Discount */}
                <div className="px-4 py-4 border-t space-y-3">
                  <p className="text-sm font-medium">Discount</p>
                  <div className="flex gap-2">
                    <Select
                      value={formData.discountType}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          discountType: value as typeof formData.discountType,
                        })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat ₹</SelectItem>
                        <SelectItem value="percentage">%</SelectItem>
                      </SelectContent>
                    </Select>
                    <NumberInput
                      placeholder="Value"
                      value={formData.discountValue}
                      onValueChange={(v) =>
                        setFormData({
                          ...formData,
                          discountValue: v,
                        })
                      }
                      className="w-32"
                    />
                  </div>
                  <Input
                    placeholder="Reason (optional)"
                    value={formData.discountReason}
                    onChange={(e) => setFormData({ ...formData, discountReason: e.target.value })}
                  />
                </div>

                {/* Shipping Cost */}
                <div className="px-4 py-4 border-t space-y-3">
                  <p className="text-sm font-medium">Shipping Cost (₹)</p>
                  <NumberInput
                    value={formData.shippingCost}
                    onValueChange={(v) => setFormData({ ...formData, shippingCost: v })}
                    className="w-32"
                  />
                </div>

                {/* Totals */}
                <div className="border-t bg-muted/50 px-4 py-3 rounded-b-lg">
                  <div className="space-y-1.5 text-sm sm:flex sm:justify-end sm:gap-8 sm:space-y-0">
                    <div className={totalLineClass}>
                      <p className="text-muted-foreground">Subtotal</p>
                      <p className="font-medium text-foreground">
                        ₹
                        {orderItems
                          .reduce(
                            (sum, item) =>
                              sum +
                              (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
                                (Number.isNaN(item.quantity) ? 0 : item.quantity),
                            0,
                          )
                          .toFixed(2)}
                      </p>
                    </div>
                    {(() => {
                      const itemSubtotal = orderItems.reduce(
                        (sum, item) =>
                          sum +
                          (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
                            (Number.isNaN(item.quantity) ? 0 : item.quantity),
                        0,
                      );
                      const discountVal = Number.isNaN(formData.discountValue)
                        ? 0
                        : formData.discountValue;
                      const disc =
                        formData.discountType === "percentage"
                          ? Math.round((itemSubtotal * discountVal) / 100)
                          : discountVal;
                      return disc > 0 ? (
                        <div className={totalLineClass}>
                          <p className="text-muted-foreground">
                            Discount
                            {formData.discountType === "percentage" &&
                              ` (${Number.isNaN(formData.discountValue) ? 0 : formData.discountValue}%)`}
                          </p>
                          <p className="font-medium text-destructive">-₹{disc}</p>
                        </div>
                      ) : null;
                    })()}
                    <div className={totalLineClass}>
                      <p className="text-muted-foreground">Shipping</p>
                      <p className="font-medium text-foreground">
                        ₹{Number.isNaN(formData.shippingCost) ? 0 : formData.shippingCost}
                      </p>
                    </div>
                    <div className={totalLineClass}>
                      <p className="text-muted-foreground">Total</p>
                      <p className={statClass}>
                        ₹
                        {(() => {
                          const itemSubtotal = orderItems.reduce(
                            (sum, item) =>
                              sum +
                              (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
                                (Number.isNaN(item.quantity) ? 0 : item.quantity),
                            0,
                          );
                          const discountVal = Number.isNaN(formData.discountValue)
                            ? 0
                            : formData.discountValue;
                          const disc =
                            formData.discountType === "percentage"
                              ? Math.round((itemSubtotal * discountVal) / 100)
                              : discountVal;
                          const shipCost = Number.isNaN(formData.shippingCost)
                            ? 0
                            : formData.shippingCost;
                          return (itemSubtotal - disc + shipCost).toFixed(2);
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {order.items && order.items.length > 0 ? (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <ul className="md:hidden divide-y divide-border">
                      {order.items.map((item) => (
                        <li
                          key={item.id}
                          className="flex items-start justify-between gap-3 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground">
                              {item.productName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.size} · {item.quantity} × ₹{item.unitPrice}
                            </div>
                          </div>
                          <span className="shrink-0 text-sm font-medium text-foreground">
                            ₹{item.lineTotal}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="hidden sm:table-cell">Size</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Line Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items.map((item, index) => (
                            <TableRow key={item.id} className="border-border">
                              <TableCell className="text-sm text-muted-foreground">
                                {index + 1}
                              </TableCell>
                              <TableCell className="text-sm text-foreground font-medium">
                                {item.productName}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                                {item.size}
                              </TableCell>
                              <TableCell className="text-sm text-foreground text-right">
                                {item.quantity}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground text-right">
                                ₹{item.unitPrice}
                              </TableCell>
                              <TableCell className="text-sm text-foreground text-right font-medium">
                                ₹{item.lineTotal}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="border-t bg-muted/50 px-4 py-3">
                      {/* Phones: one label/amount row per line; wider screens: a right-aligned strip */}
                      <div className="space-y-1.5 text-sm sm:flex sm:justify-end sm:gap-8 sm:space-y-0">
                        <div className={totalLineClass}>
                          <p className="text-muted-foreground">Subtotal</p>
                          <p className="font-medium text-foreground">₹{subtotal}</p>
                        </div>
                        {discountAmount > 0 && (
                          <div className={totalLineClass}>
                            <p className="text-muted-foreground">
                              Discount
                              {discountType === "percentage" && ` (${discountValue}%)`}
                            </p>
                            <p className="font-medium text-destructive">-₹{discountAmount}</p>
                          </div>
                        )}
                        <div className={totalLineClass}>
                          <p className="text-muted-foreground">Shipping</p>
                          <p className="font-medium text-foreground">₹{shippingCost}</p>
                        </div>
                        <div className={totalLineClass}>
                          <p className="text-muted-foreground">Total</p>
                          <p className={statClass}>₹{total}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={Package}
                    title="No items"
                    description="This order has no items yet."
                    action={
                      <Button variant="outline" size="sm" onClick={handleEditItems}>
                        <Plus className="size-4" />
                        Add Items
                      </Button>
                    }
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photo Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Photo Requests</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setPhotoDialogOpen(true)}>
              Request Photos
            </Button>
          </CardHeader>
          <CardContent>
            {order.photoRequests && order.photoRequests.length > 0 ? (
              <div className="border border-border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden sm:table-cell">Photos</TableHead>
                      <TableHead className="hidden sm:table-cell">Public Link</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.photoRequests.map((request) => (
                      <TableRow key={request.id} className="border-border">
                        <TableCell>
                          <RequestStatusBadge status={request.status} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                          {request._count.files} uploaded
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyPublicLink(request.publicLink)}
                          >
                            <Copy className="size-4" />
                            Copy
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/photo-requests/${request.id}`}>
                              <Eye className="size-4" />
                              <span className="sr-only">View request</span>
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <EmptyState
                icon={Camera}
                title="No photo requests"
                description="Create a photo request to collect images from the customer."
                action={
                  <Button variant="outline" size="sm" onClick={() => setPhotoDialogOpen(true)}>
                    <Plus className="size-4" />
                    Request Photos
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
